import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, Workflow, DollarSign } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useGoogleAdsConnection } from '@/hooks/useGoogleAdsConnection';
import { useGoogleAdsCampaigns } from '@/hooks/useGoogleAdsCampaigns';
import { GoogleAdsAccountPickerDialog } from './GoogleAdsAccountPickerDialog';
import { GoogleAdsSyncDialog } from './GoogleAdsSyncDialog';
import { GoogleAdsConversionsCard } from './GoogleAdsConversionsCard';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

const OAUTH_RETURN_REASONS: Record<string, string> = {
  no_accessible_accounts:
    'No Google Ads accounts were accessible with that login. Try connecting with the account that owns the Google Ads CID.',
  manager_only:
    'Only manager (MCC) accounts were accessible. Connect with a login that has access to a leaf Google Ads account.',
  storage_failed:
    'We received Google\'s authorization but failed to save the connection. Try again, and contact support if it persists.',
  access_denied: 'You declined the consent screen. No connection was created.',
};

/** Settings body for the Google Ads integration (OAuth connect, account picker, campaign + spend sync). */
export function GoogleAdsIntegrationPanel() {
  const {
    integration,
    isLoading,
    refetch,
    connect,
    isConnecting,
    disconnect,
    isDisconnecting,
    setActiveAccount,
    isSettingAccount,
  } = useGoogleAdsConnection();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [matchWizardOpen, setMatchWizardOpen] = useState(false);
  const { syncSpend, isSyncingSpend } = useGoogleAdsCampaigns();
  const { locale, formatShortDate } = useCompanyLocale();

  const handleSyncSpend = async () => {
    try {
      const result = await syncSpend();
      if (result.message) {
        toast({ title: 'Spend sync', description: result.message });
        return;
      }
      // Spend is in the ad account's currency; only the number formatting follows the tenant.
      const formatted = new Intl.NumberFormat(locale.locale, {
        style: 'currency',
        currency: result.currency || locale.currency,
        maximumFractionDigits: 2,
      }).format(result.total_cost);
      toast({
        title: `Synced ${result.rows_upserted} day(s) of spend`,
        description: `${result.from_date} → ${result.to_date} · ${formatted} across ${result.campaigns_synced} campaigns`,
      });
    } catch (e) {
      toast({
        title: 'Spend sync failed',
        description: e instanceof Error ? e.message.slice(0, 300) : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  // Read OAuth-return query string written by google-ads-oauth-callback,
  // show a toast, then strip the params so a refresh doesn't re-fire it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('google_ads');
    if (!status) return;

    if (status === 'connected') {
      toast({ title: 'Google Ads connected' });
      if (params.get('multiple_accounts') === '1') {
        setPickerOpen(true);
      }
      void refetch();
    } else if (status === 'error') {
      const reason = params.get('reason') ?? '';
      const friendly = OAUTH_RETURN_REASONS[reason] ?? reason ?? 'Unknown error';
      toast({
        title: 'Google Ads connection failed',
        description: friendly,
        variant: 'destructive',
      });
    }

    params.delete('google_ads');
    params.delete('reason');
    params.delete('multiple_accounts');
    const next = params.toString();
    const url = window.location.pathname + (next ? `?${next}` : '');
    window.history.replaceState({}, '', url);
  }, [refetch]);

  const handleConnect = async () => {
    try {
      await connect();
      // On success the page navigates away to Google — nothing else to do.
    } catch (e) {
      toast({
        title: 'Could not start Google Ads connection',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
      toast({ title: 'Google Ads disconnected' });
    } catch (e) {
      toast({
        title: 'Disconnect failed',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const isErrored = integration?.status === 'error' || integration?.status === 'expired';
  const accounts = integration?.metadata.available_accounts ?? [];
  const leafAccounts = accounts.filter((a) => !a.is_manager);
  const hasMultipleAccounts = leafAccounts.length > 1;

  return (
    <>
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Pull campaign spend from Google Ads to attribute jobs and measure cost per job, and send
          your leads and paid jobs back to Google as conversions so bidding learns what a real job looks like.
        </p>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading status...
          </div>
        ) : !integration || integration.status === 'revoked' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Click below to authorize this app to read campaign data from your Google Ads
              account. We only request the <code className="px-1 py-0.5 bg-muted rounded">adwords</code> scope.
            </p>
            <Button onClick={handleConnect} disabled={isConnecting}>
              {isConnecting ? 'Redirecting to Google...' : 'Connect Google Ads'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {isErrored && (
              <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Connection needs attention</div>
                  <div className="text-muted-foreground">
                    {integration.status_message ||
                      integration.last_error ||
                      'Reconnect to restore access.'}
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg border p-3 space-y-1">
              <div className="text-sm font-medium">
                {integration.metadata.descriptive_name || 'Unnamed Google Ads account'}
              </div>
              <div className="text-xs text-muted-foreground font-mono">
                Customer ID {integration.metadata.customer_id ?? '— not set —'}
              </div>
              <div className="text-xs text-muted-foreground">
                {integration.metadata.timezone ?? 'Unknown TZ'} ·{' '}
                {integration.metadata.currency_code ?? 'Unknown currency'}
              </div>
              <div className="text-xs text-muted-foreground">
                Connected {formatShortDate(integration.connected_at)}
              </div>
            </div>

            {integration.status === 'connected' && (
              <GoogleAdsConversionsCard integration={integration} />
            )}

            {!integration.metadata.customer_id && hasMultipleAccounts && (
              <div className="flex gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
                <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">Multiple accounts available</div>
                  <div className="text-muted-foreground">
                    Pick which Google Ads account this company reports on.
                  </div>
                </div>
                <Button size="sm" onClick={() => setPickerOpen(true)}>
                  Choose account
                </Button>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {integration.metadata.customer_id && (
                <Button size="sm" onClick={() => setMatchWizardOpen(true)}>
                  <Workflow className="h-4 w-4 mr-1" /> Sync campaigns
                </Button>
              )}
              {integration.metadata.customer_id && (
                <Button size="sm" variant="secondary" onClick={handleSyncSpend} disabled={isSyncingSpend}>
                  <DollarSign className="h-4 w-4 mr-1" />
                  {isSyncingSpend ? 'Syncing spend…' : 'Sync spend'}
                </Button>
              )}
              {hasMultipleAccounts && integration.metadata.customer_id && (
                <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                  Switch account
                </Button>
              )}
              {isErrored && (
                <Button variant="outline" size="sm" onClick={handleConnect} disabled={isConnecting}>
                  {isConnecting ? 'Redirecting...' : 'Reconnect'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDisconnect}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </div>
          </div>
        )}
      </div>

      <GoogleAdsAccountPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        accounts={accounts}
        currentCustomerId={integration?.metadata.customer_id ?? null}
        onSelect={setActiveAccount}
        isSaving={isSettingAccount}
      />

      <GoogleAdsSyncDialog
        open={matchWizardOpen}
        onOpenChange={setMatchWizardOpen}
      />
    </>
  );
}
